"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.meetingTypeThunks = void 0;
const constants_1 = require("@/app/shared/constants");
const actions_1 = require("./actions");
const constants_2 = require("./constants");
const selectors_1 = require("./selectors");
const utils_1 = require("./utils");
const applySelectedMeetingTypeThunk = () => (dispatch, getState) => {
    const state = getState();
    const meetingType = selectors_1.schedulerSelectors.selectMeetingType(state);
    const selectedMeetingType = selectors_1.schedulerSelectors.selectSelectedMeetingType(state);
    if (selectedMeetingType) {
        // Apply settings from selected meeting type or managed meeting
        dispatch(actions_1.SUMO1SchedulerActions.updateBookingPage({
            allUsers: selectedMeetingType?.allUsers,
            inputFields: selectedMeetingType.inputFields,
            what: {
                ...meetingType.what,
                customName: selectedMeetingType.what?.customName || '',
                instructions: selectedMeetingType.what?.instructions || '',
                color: selectedMeetingType.what?.color,
            },
            who: selectedMeetingType.who,
            where: selectedMeetingType.where
                ? {
                    ...selectedMeetingType.where,
                    locationTypes: [selectedMeetingType.where?.defaultLocationType || null],
                }
                : meetingType.where,
            when: selectedMeetingType?.when,
            labels: selectedMeetingType?.labels,
            style: selectedMeetingType?.style,
            confirmationsPage: selectedMeetingType.confirmationsPage,
            notifications: selectedMeetingType.notifications,
            calendar: selectedMeetingType?.calendar,
            inviteOthers: selectedMeetingType?.inviteOthers,
            enterNote: selectedMeetingType?.enterNote,
            smartAlertIds: selectedMeetingType?.smartAlertIds,
            meetingPayment: selectedMeetingType?.meetingPayment,
        }));
        // Handle bucket-based host and guest assignment
        const membersById = selectors_1.schedulerSelectors.selectMembersById(state);
        const allBuckets = selectedMeetingType.who?.guestsBuckets || [];
        // First, identify all round-robin buckets and assign pool numbers sequentially
        let poolCounter = 1;
        const roundRobinBucketPoolNumbers = new Map();
        allBuckets.forEach((bucket, index) => {
            if (bucket?.bucketType === constants_2.ROUND_ROBIN_BUCKET_TYPE) {
                roundRobinBucketPoolNumbers.set(index, poolCounter);
                poolCounter++;
            }
        });
        // Handle host assignment
        const hostBucketIndex = allBuckets.findIndex((bucket) => bucket?.isHost);
        const hostBucket = allBuckets[hostBucketIndex];
        let newHost = null;
        if (hostBucket) {
            if (hostBucket.bucketType === constants_2.STANDARD_BUCKET_TYPE) {
                // For standard host bucket, resolve first member using membersById
                const firstMemberId = hostBucket.members?.[0]?.id;
                newHost = (firstMemberId && membersById[firstMemberId]) || null;
            }
            else {
                // For round-robin host bucket, use the assigned pool number
                const poolNumber = roundRobinBucketPoolNumbers.get(hostBucketIndex);
                const bucketName = `Pool ${poolNumber}`;
                newHost = (0, utils_1.convertGuestBucketToSchedulerBucketLocal)(hostBucket, bucketName);
            }
            dispatch(actions_1.SUMO1SchedulerActions.setHost(newHost));
        }
        // Handle guest assignment
        const guestBuckets = allBuckets.filter((bucket) => !bucket?.isHost);
        const newGuests = [];
        guestBuckets.forEach((bucket) => {
            const originalIndex = allBuckets.findIndex((b) => b === bucket);
            if (bucket?.bucketType === constants_2.STANDARD_BUCKET_TYPE) {
                // For standard buckets, resolve first members using membersById
                const newMember = membersById[bucket.members?.[0]?.id || ''];
                if (newMember) {
                    newGuests.push(newMember);
                }
            }
            else {
                // For round-robin buckets, convert to SchedulerBucketLocal with correct pool number
                const poolNumber = roundRobinBucketPoolNumbers.get(originalIndex);
                const bucketName = `Pool ${poolNumber}`;
                const newBucket = bucket && (0, utils_1.convertGuestBucketToSchedulerBucketLocal)(bucket, bucketName);
                if (newBucket) {
                    newGuests.push(newBucket);
                }
            }
        });
        // Set main guest from the first eligible guest
        let newMainGuest = null;
        for (const guest of newGuests) {
            if (!constants_1.STAR_OPTIONS.includes(guest.subType))
                continue;
            // Teams are always eligible as main guest
            if (guest.subType === "TEAM" /* MemberSubType.TEAM */) {
                newMainGuest = guest;
                break;
            }
            // Skip if guest is the same user as the host
            if (newHost && guest.id === newHost.id)
                continue;
            newMainGuest = guest;
            break;
        }
        // Remove main guest from the guests list
        const finalGuests = newMainGuest ? newGuests.filter((g) => g.id !== newMainGuest.id) : newGuests;
        dispatch(actions_1.SUMO1SchedulerActions.setMainGuest(newMainGuest));
        dispatch(actions_1.SUMO1SchedulerActions.setGuests(finalGuests));
        // Trigger host schedule update
        dispatch(actions_1.SUMO1SchedulerActions.getHostScheduleRequest());
    }
    else {
        // No meeting type selected - remove all bucket members from host and guests
        const currentHost = selectors_1.schedulerSelectors.selectHost(state);
        const currentGuests = selectors_1.schedulerSelectors.selectGuests(state);
        // Remove host if it's a bucket
        if (currentHost && currentHost.subType === "BUCKET" /* MemberSubType.BUCKET */) {
            dispatch(actions_1.SUMO1SchedulerActions.setHost(null));
        }
        // Filter out bucket members from guests
        const filteredGuests = currentGuests.filter((guest) => {
            return guest.subType !== "BUCKET" /* MemberSubType.BUCKET */;
        });
        // Only update guests if any were filtered out
        if (filteredGuests.length !== currentGuests.length) {
            dispatch(actions_1.SUMO1SchedulerActions.setGuests(filteredGuests));
        }
    }
};
const handleGuestsChangeThunk = (members) => (dispatch, getState) => {
    const state = getState();
    const mainGuest = selectors_1.schedulerSelectors.selectMainGuest(state);
    const guests = selectors_1.schedulerSelectors.selectGuests(state);
    const guestOptions = selectors_1.schedulerSelectors.selectGuestOptions(state);
    const newMainGuest = members.find((member) => constants_1.STAR_OPTIONS.includes(member.subType)) || null;
    const newGuests = members.filter((member) => member.id !== newMainGuest?.id);
    if (newMainGuest !== mainGuest) {
        dispatch(actions_1.SUMO1SchedulerActions.setMainGuest(newMainGuest));
    }
    if (newGuests !== guests) {
        dispatch(actions_1.SUMO1SchedulerActions.setGuests(newGuests));
    }
    // Find and add new custom members to guestOptions
    const guestOptionIds = new Set(guestOptions.map((option) => option.id));
    const newCustomMembers = members.filter((member) => member.type === "CUSTOM" /* MemberType.CUSTOM */ && !guestOptionIds.has(member.id));
    if (newCustomMembers.length > 0) {
        dispatch(actions_1.SUMO1SchedulerActions.addMembersAction(newCustomMembers));
    }
    dispatch(actions_1.SUMO1SchedulerActions.getHostScheduleRequest());
};
const switchHeaderTabThunk = (headerTab) => (dispatch, getState) => {
    const state = getState();
    dispatch(actions_1.SUMO1SchedulerActions.setHeaderTab(headerTab));
    if (headerTab === "INSTANT_MEETING" /* SchedulerHeaderTab.INSTANT_MEETING */) {
        const inPersonType = selectors_1.schedulerSelectors.selectInPersonType(state);
        const phoneCallType = selectors_1.schedulerSelectors.selectPhoneCallType(state);
        const updates = {};
        if (inPersonType === constants_1.InPersonType.INVITEE_LOCATION) {
            updates.inPersonType = constants_1.InPersonType.CUSTOM_ADDRESS;
            updates.inPersonTypes = [constants_1.InPersonType.CUSTOM_ADDRESS];
        }
        if (phoneCallType === constants_1.PhoneCallType.PROMPT_INVITE) {
            updates.phoneCallType = constants_1.PhoneCallType.HOST_PHONE_NUMBER;
        }
        if (Object.keys(updates).length) {
            dispatch(actions_1.SUMO1SchedulerActions.updateBookingPageWhere(updates));
        }
    }
};
exports.meetingTypeThunks = {
    applySelectedMeetingTypeThunk,
    handleGuestsChangeThunk,
    switchHeaderTabThunk,
};
